import {
	styled,
	Card as MuiCard,
	CardContent,
	CardProps,
	Stack,
	Typography,
	useTheme,
} from '@mui/material';

import GeneralLoader from '~components/loaders/generalLoader';

import Dimens from '../../themes/dimens';

interface StyledCardProps extends CardProps {
	title?: string;
	caption?: string;
	children?: React.ReactNode;
	loading?: boolean;
	density?: 'normal' | 'compact' | 'dense';
}

const Card = ({
	density = 'normal',
	title,
	caption,
	loading = false,
	...props
}: StyledCardProps) => {
	const { palette } = useTheme();

	const spacing =
		density === 'dense' ?
			{
				xs: Dimens.spacingMd,
				md: Dimens.spacingMd,
			}
		: density === 'compact' ?
			{
				xs: Dimens.spacingXl,
				md: Dimens.spacingXl,
			}
		:	{
				xs: Dimens.spacingLg,
				md: Dimens.spacing3xl,
			};

	return (
		<StyledCard
			elevation={0}
			density={density}
			{...props}
			sx={{
				padding: spacing,
			}}
		>
			{(title || caption) && (
				<Stack
					direction={{
						xs: 'column',
						md: 'row',
					}}
					justifyContent={{
						xs: 'start',
						md: 'space-between',
					}}
					alignItems={{
						xs: 'start',
						md: 'space-between',
					}}
					sx={{ pb: Dimens.spacingXl, minWidth: 0 }}
				>
					<Typography variant='labelLg' noWrap>
						{title}
					</Typography>
					<Typography variant='paragraphXs' color={palette.textQuaternary.main}>
						{caption}
					</Typography>
				</Stack>
			)}
			<StyledCardContent sx={{ height: '100%' }}>
				{loading ?
					<GeneralLoader />
				:	props.children}
			</StyledCardContent>
		</StyledCard>
	);
};

interface StyledCardStyleProps {
	density: 'normal' | 'compact' | 'dense';
}

const StyledCard = styled(MuiCard, {
	shouldForwardProp: (prop) => prop !== 'density',
})<StyledCardStyleProps>(({ theme }) => {
	const palette = theme.palette;

	return {
		borderRadius: Dimens.radius2xl,
		border: '1px solid',
		borderColor: palette.borderTertiary.main,
		background: palette.bgPrimary.main,
		// Not a big fan of styled() anymore. This seems to be recurring now that it needs both.
		'&.MuiCardContent-root:last-child': {
			paddingBottom: 0,
		},
		'& .MuiCardContent-root:last-child': {
			paddingBottom: 0,
		},
	};
});

const StyledCardContent = styled(CardContent)(() => {
	return {
		padding: Dimens.spacingNone,
	};
});

export default Card;
